import { useEffect, useMemo, useRef, useState } from 'react';
import { Captions, Film, Loader2, Play, SkipBack, SkipForward } from 'lucide-react';
import { Button } from '../ui/button';
import { absUrl } from '../../lib/aiStudioApi';
import { isRendering } from './useMovieProject';
import { cn } from '../../lib/utils';

/** Sequential cinema player — plays every finished shot back-to-back with synced voice-over + subtitles. */
export function ScreeningRoom({ project }) {
  const eps = project.format === 'series' ? project.episodes_count || 1 : 1;
  const [episode, setEpisode] = useState(1);
  const [idx, setIdx] = useState(0);
  const [subs, setSubs] = useState(true);
  const [autoplay, setAutoplay] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const shots = useMemo(() => (project.scenes || []).filter((s) => (s.episode || 1) === episode), [project.scenes, episode]);
  const playable = shots.filter((s) => s.video_url);
  const current = playable[Math.min(idx, Math.max(0, playable.length - 1))];

  useEffect(() => { setIdx(0); }, [episode]);
  useEffect(() => { if (autoplay) videoRef.current?.play().catch(() => {}); }, [current?.id, autoplay]);

  const syncAudio = (action) => {
    const a = audioRef.current; const v = videoRef.current;
    if (!a || !current?.audio_url) return;
    if (action === 'play') { a.currentTime = v?.currentTime || 0; a.play().catch(() => {}); } else a.pause();
  };
  const next = () => { if (idx < playable.length - 1) { setIdx(idx + 1); setAutoplay(true); } else setAutoplay(false); };

  return (
    <div className="space-y-4" data-testid="screening-room">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {eps > 1 && Array.from({ length: eps }, (_, i) => (
            <button key={i + 1} type="button" onClick={() => setEpisode(i + 1)} data-testid={`screening-episode-${i + 1}`}
              className={cn('rounded-md px-2.5 py-1 font-mono text-xs', episode === i + 1 ? 'bg-amber-500 text-neutral-900' : 'bg-neutral-900 text-neutral-400 hover:text-neutral-200')}>
              EP{String(i + 1).padStart(2, '0')}
            </button>
          ))}
          <span className="text-xs text-neutral-500" data-testid="screening-ready-count">{playable.length}/{shots.length} shots ready</span>
        </div>
        <span className="text-[11px] text-neutral-500">Preview — voices play over each shot. The Final Cut makes the mix permanent.</span>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-black shadow-[0_40px_120px_-40px_rgba(245,158,11,0.25)]">
        {current ? (
          <>
            <video key={current.id} ref={videoRef} src={absUrl(current.video_url)} poster={current.still_url || undefined} controls playsInline
              onPlay={() => syncAudio('play')} onPause={() => syncAudio('pause')} onEnded={next}
              className="mx-auto max-h-[70vh] w-full bg-black object-contain" data-testid="screening-player" />
            {current.audio_url && <audio key={`a-${current.id}`} ref={audioRef} src={absUrl(current.audio_url)} className="hidden" />}
            <div className="pointer-events-none absolute left-4 top-4 rounded-md bg-black/60 px-2.5 py-1 text-[11px] text-neutral-200 backdrop-blur">
              EP{episode} · Shot {playable.indexOf(current) + 1} — {current.title || 'Untitled'}
            </div>
            {subs && (current.dialogue || current.narration) && (
              <div className="pointer-events-none absolute inset-x-0 bottom-16 text-center" data-testid="screening-subtitles">
                <span className="rounded bg-black/70 px-3 py-1 text-sm text-white">{current.dialogue ? `${current.speaker ? `${current.speaker}: ` : ''}${current.dialogue}` : current.narration}</span>
                {current.dialogue_en && current.dialogue_en !== current.dialogue && <div className="mt-1"><span className="rounded bg-black/60 px-2 py-0.5 text-xs text-neutral-300">{current.dialogue_en}</span></div>}
              </div>
            )}
          </>
        ) : (
          <div className="flex aspect-video flex-col items-center justify-center gap-3 text-center text-neutral-500" data-testid="screening-empty">
            <Film size={28} />
            <div className="text-sm text-neutral-300">Nothing to screen yet</div>
            <div className="max-w-sm text-xs">Hit <strong>Produce all</strong> — finished shots stream into this theatre live while the rest keep rendering.</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="text-neutral-300" data-testid="screening-prev"><SkipBack size={15} /></Button>
        <Button size="sm" onClick={() => { setIdx(0); setAutoplay(true); videoRef.current?.play().catch(() => {}); }} disabled={!playable.length}
          className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="screening-play-all"><Play size={14} className="mr-1" /> Play all</Button>
        <Button size="sm" variant="ghost" onClick={next} disabled={idx >= playable.length - 1} className="text-neutral-300" data-testid="screening-next"><SkipForward size={15} /></Button>
        <Button size="sm" variant="ghost" onClick={() => setSubs(!subs)} className={cn(subs ? 'text-amber-300' : 'text-neutral-500')} data-testid="screening-subs-toggle"><Captions size={15} /></Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="screening-filmstrip">
        {shots.map((s, i) => {
          const pIndex = playable.indexOf(s);
          return (
            <button key={s.id} type="button" disabled={pIndex < 0} onClick={() => { setIdx(pIndex); setAutoplay(true); }} data-testid={`screening-strip-${s.id}`}
              className={cn('relative h-20 w-32 shrink-0 overflow-hidden rounded-md border bg-neutral-900 text-left',
                current?.id === s.id ? 'border-amber-400' : 'border-neutral-800', pIndex < 0 && 'cursor-default')}>
              {s.still_url && <img src={s.still_url} alt="" className={cn('h-full w-full object-cover', !s.video_url && 'opacity-50')} />}
              <span className="absolute left-1 top-1 rounded bg-black/70 px-1 font-mono text-[9px] text-neutral-200">{String(i + 1).padStart(2, '0')}</span>
              {isRendering(s) && <span className="absolute inset-0 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-amber-300" /></span>}
              {!s.still_url && !isRendering(s) && <span className="absolute inset-0 flex items-center justify-center text-[10px] text-neutral-600">pending</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
